/**
 * Major container ports (UN/LOCODE) for the origin/destination box: matches by
 * code, name or country that show instantly while typing, and let a typed code
 * be recognised without a lookup. The airports and ports found near a place
 * come from the backend (`places/resolve/`), which has its own, larger
 * dataset; keep this list's codes present there. Names are common English
 * port names.
 */
export interface StaticOceanPort {
  code: string;
  name: string;
  country: string;
}

export const MAJOR_OCEAN_PORTS: StaticOceanPort[] = [
  // North America — US
  { code: 'USNYC', name: 'New York / New Jersey', country: 'United States' },
  { code: 'USLAX', name: 'Los Angeles', country: 'United States' },
  { code: 'USLGB', name: 'Long Beach', country: 'United States' },
  { code: 'USSAV', name: 'Savannah', country: 'United States' },
  { code: 'USHOU', name: 'Houston', country: 'United States' },
  { code: 'USCHS', name: 'Charleston', country: 'United States' },
  { code: 'USORF', name: 'Norfolk', country: 'United States' },
  { code: 'USSEA', name: 'Seattle', country: 'United States' },
  { code: 'USTIW', name: 'Tacoma', country: 'United States' },
  { code: 'USOAK', name: 'Oakland', country: 'United States' },
  { code: 'USBAL', name: 'Baltimore', country: 'United States' },
  { code: 'USMIA', name: 'Miami', country: 'United States' },
  { code: 'USPEF', name: 'Port Everglades', country: 'United States' },
  { code: 'USJAX', name: 'Jacksonville', country: 'United States' },
  { code: 'USMSY', name: 'New Orleans', country: 'United States' },
  { code: 'USPHL', name: 'Philadelphia', country: 'United States' },
  { code: 'USBOS', name: 'Boston', country: 'United States' },
  // North America — Canada / Mexico
  { code: 'CAVAN', name: 'Vancouver', country: 'Canada' },
  { code: 'CAMTR', name: 'Montreal', country: 'Canada' },
  { code: 'CAHAL', name: 'Halifax', country: 'Canada' },
  { code: 'CAPRR', name: 'Prince Rupert', country: 'Canada' },
  { code: 'MXVER', name: 'Veracruz', country: 'Mexico' },
  { code: 'MXZLO', name: 'Manzanillo', country: 'Mexico' },
  { code: 'MXATM', name: 'Altamira', country: 'Mexico' },
  // Europe
  { code: 'NLRTM', name: 'Rotterdam', country: 'Netherlands' },
  { code: 'DEHAM', name: 'Hamburg', country: 'Germany' },
  { code: 'BEANR', name: 'Antwerp', country: 'Belgium' },
  { code: 'DEBRV', name: 'Bremerhaven', country: 'Germany' },
  { code: 'FRLEH', name: 'Le Havre', country: 'France' },
  { code: 'FRFOS', name: 'Fos-sur-Mer (Marseille)', country: 'France' },
  { code: 'GBFXT', name: 'Felixstowe', country: 'United Kingdom' },
  { code: 'GBLGP', name: 'London Gateway', country: 'United Kingdom' },
  { code: 'GBSOU', name: 'Southampton', country: 'United Kingdom' },
  { code: 'GBLIV', name: 'Liverpool', country: 'United Kingdom' },
  { code: 'IEDUB', name: 'Dublin', country: 'Ireland' },
  { code: 'ESVLC', name: 'Valencia', country: 'Spain' },
  { code: 'ESALG', name: 'Algeciras', country: 'Spain' },
  { code: 'ESBCN', name: 'Barcelona', country: 'Spain' },
  { code: 'PTSIE', name: 'Sines', country: 'Portugal' },
  { code: 'ITGOA', name: 'Genoa', country: 'Italy' },
  { code: 'ITSPE', name: 'La Spezia', country: 'Italy' },
  { code: 'ITGIT', name: 'Gioia Tauro', country: 'Italy' },
  { code: 'GRPIR', name: 'Piraeus', country: 'Greece' },
  { code: 'MTMAR', name: 'Marsaxlokk', country: 'Malta' },
  { code: 'PLGDN', name: 'Gdansk', country: 'Poland' },
  { code: 'SEGOT', name: 'Gothenburg', country: 'Sweden' },
  { code: 'TRAMR', name: 'Ambarli (Istanbul)', country: 'Turkey' },
  { code: 'TRMER', name: 'Mersin', country: 'Turkey' },
  // Middle East / Africa
  { code: 'AEJEA', name: 'Jebel Ali (Dubai)', country: 'United Arab Emirates' },
  { code: 'AEAUH', name: 'Abu Dhabi', country: 'United Arab Emirates' },
  { code: 'SAJED', name: 'Jeddah', country: 'Saudi Arabia' },
  { code: 'OMSLL', name: 'Salalah', country: 'Oman' },
  { code: 'EGPSD', name: 'Port Said', country: 'Egypt' },
  { code: 'EGALY', name: 'Alexandria', country: 'Egypt' },
  { code: 'MAPTM', name: 'Tanger Med', country: 'Morocco' },
  { code: 'ZADUR', name: 'Durban', country: 'South Africa' },
  { code: 'ZACPT', name: 'Cape Town', country: 'South Africa' },
  { code: 'KEMBA', name: 'Mombasa', country: 'Kenya' },
  { code: 'NGAPP', name: 'Apapa (Lagos)', country: 'Nigeria' },
  // Asia
  { code: 'CNSHA', name: 'Shanghai', country: 'China' },
  { code: 'CNNGB', name: 'Ningbo', country: 'China' },
  { code: 'CNYTN', name: 'Yantian (Shenzhen)', country: 'China' },
  { code: 'CNSHK', name: 'Shekou (Shenzhen)', country: 'China' },
  { code: 'CNTAO', name: 'Qingdao', country: 'China' },
  { code: 'CNTXG', name: 'Tianjin', country: 'China' },
  { code: 'CNXMN', name: 'Xiamen', country: 'China' },
  { code: 'CNCAN', name: 'Guangzhou (Nansha)', country: 'China' },
  { code: 'HKHKG', name: 'Hong Kong', country: 'Hong Kong' },
  { code: 'SGSIN', name: 'Singapore', country: 'Singapore' },
  { code: 'KRPUS', name: 'Busan', country: 'South Korea' },
  { code: 'JPTYO', name: 'Tokyo', country: 'Japan' },
  { code: 'JPYOK', name: 'Yokohama', country: 'Japan' },
  { code: 'JPUKB', name: 'Kobe', country: 'Japan' },
  { code: 'JPNGO', name: 'Nagoya', country: 'Japan' },
  { code: 'TWKHH', name: 'Kaohsiung', country: 'Taiwan' },
  { code: 'VNSGN', name: 'Ho Chi Minh City (Cat Lai)', country: 'Vietnam' },
  { code: 'VNCMT', name: 'Cai Mep', country: 'Vietnam' },
  { code: 'VNHPH', name: 'Haiphong', country: 'Vietnam' },
  { code: 'THLCH', name: 'Laem Chabang', country: 'Thailand' },
  { code: 'MYPKG', name: 'Port Klang', country: 'Malaysia' },
  { code: 'MYTPP', name: 'Tanjung Pelepas', country: 'Malaysia' },
  { code: 'IDJKT', name: 'Jakarta (Tanjung Priok)', country: 'Indonesia' },
  { code: 'INNSA', name: 'Nhava Sheva (Mumbai)', country: 'India' },
  { code: 'INMUN', name: 'Mundra', country: 'India' },
  { code: 'INMAA', name: 'Chennai', country: 'India' },
  { code: 'LKCMB', name: 'Colombo', country: 'Sri Lanka' },
  { code: 'PKQCT', name: 'Karachi (Qasim)', country: 'Pakistan' },
  { code: 'BDCGP', name: 'Chattogram', country: 'Bangladesh' },
  // South America
  { code: 'BRSSZ', name: 'Santos', country: 'Brazil' },
  { code: 'BRRIG', name: 'Rio Grande', country: 'Brazil' },
  { code: 'BRPNG', name: 'Paranagua', country: 'Brazil' },
  { code: 'ARBUE', name: 'Buenos Aires', country: 'Argentina' },
  { code: 'CLVAP', name: 'Valparaiso', country: 'Chile' },
  { code: 'CLSAI', name: 'San Antonio', country: 'Chile' },
  { code: 'PECLL', name: 'Callao', country: 'Peru' },
  { code: 'COCTG', name: 'Cartagena', country: 'Colombia' },
  { code: 'ECGYE', name: 'Guayaquil', country: 'Ecuador' },
  { code: 'PAMIT', name: 'Manzanillo (Colon)', country: 'Panama' },
  { code: 'PABLB', name: 'Balboa', country: 'Panama' },
  // Oceania
  { code: 'AUSYD', name: 'Sydney', country: 'Australia' },
  { code: 'AUMEL', name: 'Melbourne', country: 'Australia' },
  { code: 'AUBNE', name: 'Brisbane', country: 'Australia' },
  { code: 'AUFRE', name: 'Fremantle', country: 'Australia' },
  { code: 'NZAKL', name: 'Auckland', country: 'New Zealand' },
];

/** Case-insensitive match on UNLOCODE, port name, or country. */
export function searchStaticPorts(query: string, limit = 8): StaticOceanPort[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const codeMatches: StaticOceanPort[] = [];
  const nameMatches: StaticOceanPort[] = [];
  for (const port of MAJOR_OCEAN_PORTS) {
    if (port.code.toLowerCase().startsWith(q)) {
      codeMatches.push(port);
    } else if (
      port.name.toLowerCase().includes(q) ||
      port.country.toLowerCase().includes(q)
    ) {
      nameMatches.push(port);
    }
  }
  return [...codeMatches, ...nameMatches].slice(0, limit);
}
